import React from 'react';
import { User, Calendar, Wrench, DollarSign, MapPin, Mail } from 'lucide-react';

/**
 * GlobalSearchDropdown Component
 * Displays search results grouped by entity type
 */
const GlobalSearchDropdown = ({ results, loading, error, query, onSelectResult, onRetry, selectedIndex }) => {
  // Don't show dropdown if no query or if query is too short
  if (!query || query.length < 2) {
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
        <div className="flex items-center justify-center space-x-2 text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-800"></div>
          <span className="text-sm">Searching...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
        <div className="text-center">
          <p className="text-sm text-red-600 mb-2">{error}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-xs text-red-800 hover:text-red-900 font-medium"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Count total results
  const totalResults = Object.values(results).reduce((acc, arr) => acc + arr.length, 0);

  // Show empty state
  if (totalResults === 0) {
    return (
      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
        <p className="text-sm text-gray-500 text-center">
          No results found for "{query}"
        </p>
      </div>
    );
  }

  // Flatten results for keyboard navigation
  const flatResults = [];
  Object.entries(results).forEach(([entityType, items]) => {
    items.forEach((item) => {
      flatResults.push({ ...item, entityType });
    });
  });

  const renderIcon = (type) => {
    const iconClass = "w-4 h-4";
    switch (type) {
      case 'clients':
        return <User className={iconClass} />;
      case 'appointments':
        return <Calendar className={iconClass} />;
      case 'technicians':
        return <Wrench className={iconClass} />;
      case 'services':
        return <DollarSign className={iconClass} />;
      case 'locations':
        return <MapPin className={iconClass} />;
      case 'leads':
        return <Mail className={iconClass} />;
      default:
        return null;
    }
  };

  const renderResultItem = (item, entityType, index) => {
    const isSelected = index === selectedIndex;
    const baseClass = "px-3 py-2 cursor-pointer transition-colors";
    const selectedClass = isSelected ? "bg-red-50" : "hover:bg-gray-50";

    switch (entityType) {
      case 'clients':
        return (
          <div
            key={`client-${item.id}`}
            className={`${baseClass} ${selectedClass}`}
            onClick={() => onSelectResult(item, entityType)}
          >
            <div className="flex items-start space-x-2">
              <User className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {item.person_name || 'Unknown'}
                </p>
                {item.total_spent !== null && item.total_spent !== undefined && (
                  <p className="text-xs text-gray-500 truncate">Total: ${parseFloat(item.total_spent).toFixed(2)}</p>
                )}
                {item.location_name && (
                  <p className="text-xs text-gray-400 truncate">{item.location_name}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'appointments':
        return (
          <div
            key={`appointment-${item.id}`}
            className={`${baseClass} ${selectedClass}`}
            onClick={() => onSelectResult(item, entityType)}
          >
            <div className="flex items-start space-x-2">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {new Date(item.date_time).toLocaleDateString()} - {item.client_name || 'Unknown Client'}
                </p>
                {item.technician_name && (
                  <p className="text-xs text-gray-500 truncate">Team: {item.technician_name}</p>
                )}
                {item.status && (
                  <p className="text-xs text-gray-400 capitalize truncate">{item.status}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'technicians':
        return (
          <div
            key={`technician-${item.id}`}
            className={`${baseClass} ${selectedClass}`}
            onClick={() => onSelectResult(item, entityType)}
          >
            <div className="flex items-start space-x-2">
              <Wrench className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {item.person_name || 'Unknown'}
                </p>
                {item.square_status && (
                  <p className="text-xs text-gray-500 capitalize truncate">{item.square_status.replace('_', ' ')}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'services':
        return (
          <div
            key={`service-${item.id}`}
            className={`${baseClass} ${selectedClass}`}
            onClick={() => onSelectResult(item, entityType)}
          >
            <div className="flex items-start space-x-2">
              <DollarSign className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                {item.price && (
                  <p className="text-xs text-gray-500 truncate">${item.price}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'locations':
        return (
          <div
            key={`location-${item.id}`}
            className={`${baseClass} ${selectedClass}`}
            onClick={() => onSelectResult(item, entityType)}
          >
            <div className="flex items-start space-x-2">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                {item.address && (
                  <p className="text-xs text-gray-500 truncate">{item.address}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'leads':
        return (
          <div
            key={`lead-${item.id}`}
            className={`${baseClass} ${selectedClass}`}
            onClick={() => onSelectResult(item, entityType)}
          >
            <div className="flex items-start space-x-2">
              <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {item.full_name || item.person?.full_name || 'Unknown'}
                </p>
                {(item.phone_number || item.person?.phone_number) && (
                  <p className="text-xs text-gray-500 truncate">{item.phone_number || item.person?.phone_number}</p>
                )}
                {item.email && (
                  <p className="text-xs text-gray-400 truncate">{item.email}</p>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const entityLabels = {
    clients: 'CLIENTS',
    leads: 'CONTACTS',
    appointments: 'APPOINTMENTS',
    technicians: 'TEAM MEMBERS',
    services: 'SERVICES',
    locations: 'LOCATIONS',
  };

  return (
    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
      {Object.keys(entityLabels).map((entityType) => {
        const items = results[entityType] || [];
        if (items.length === 0) return null;

        // Calculate starting index for this group
        const startIndex = flatResults.findIndex(r => r.entityType === entityType);

        return (
          <div key={entityType} className="border-b border-gray-100 last:border-b-0">
            <div className="px-3 py-2 bg-gray-50 flex items-center space-x-2">
              {renderIcon(entityType)}
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                {entityLabels[entityType]} ({items.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-100">
              {items.map((item, idx) => renderResultItem(item, entityType, startIndex + idx))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GlobalSearchDropdown;
